import React, { useCallback } from 'react'
import { number, object, string } from 'yup'
import { Button, Grid, MenuItem } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useFormContext } from 'react-hook-form'
import uniqueId from 'lodash/uniqueId'

import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  SelectFieldControl,
  SubmitButton,
  TextFieldControl,
} from '../form'
import { Dialog } from '../dialog.tsx'
import { RefundFormState, UniqueOfdPosition } from '../refund-sbp-button/refund-button.types.ts'

import { OFD_PAYMENT_METHODS, OFD_SUBJECT_TYPE, OFD_TAXES } from './ofd-fields.constants.ts'

const positionFormSchema = object<FormSchema<UniqueOfdPosition>>({
  id: string().required(),
  quantity: number()
    .transform((_, orig) => (orig === '' ? undefined : +orig))
    .required(FormSchemaMessages.required)
    .test(FormSchemaTests.isNotZero),
  price: string().required(FormSchemaMessages.required).test(FormSchemaTests.isNotZero),
  tax: number().required(FormSchemaMessages.required),
  text: string().required(FormSchemaMessages.required).max(128),
  paymentMethodType: number().required(FormSchemaMessages.required),
  paymentSubjectType: number().required(FormSchemaMessages.required),
  nomenclatureCode: string(),
  unitOfMeasurement: string().min(1).max(16),
})

export interface OfdPositionDialogProps {
  position?: UniqueOfdPosition
  open: boolean
  onClose(): void
}
export const OfdPositionDialog = ({ position, open, onClose }: OfdPositionDialogProps): React.ReactElement | null => {
  const isEditingPosition = !isEmpty(position)
  const { getValues, setValue } = useFormContext<RefundFormState>()

  const handleSavePosition = useCallback(
    ({ quantity, ...formState }: UniqueOfdPosition) => {
      let positions = getValues('positions') ?? []

      if (isEditingPosition) {
        positions = positions.map((position) =>
          position.id === formState.id ? { ...formState, quantity: Number(quantity) } : position
        )
      } else {
        positions.push({ ...formState, quantity: Number(quantity) })
      }

      setValue('positions', positions, { shouldValidate: true })
      onClose()
    },
    [isEditingPosition, getValues, setValue]
  )

  if (!open) return null

  return (
    <Form<UniqueOfdPosition>
      validationSchema={positionFormSchema}
      name="positionForm"
      initialValues={position ? position : { id: uniqueId() }}
      onSubmit={handleSavePosition}
    >
      <Dialog
        title={isEditingPosition ? 'Редактирование позиции' : 'Добавление позиции'}
        open
        onClose={onClose}
        content={
          <Grid container spacing={2} direction="column">
            <Grid item>
              <TextFieldControl name="text" label="Наименование предмета расчета" fullWidth />
            </Grid>
            <Grid item container direction="row" spacing={2}>
              <Grid item xs={12} md={6}>
                <TextFieldControl
                  name="quantity"
                  label="Количество"
                  fullWidth
                  normalize={InputNormalizers.positiveNumbersOnly(6)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldControl
                  name="price"
                  label="Цена за единицу предмета"
                  fullWidth
                  normalize={InputNormalizers.positiveNumbersOnly()}
                />
              </Grid>
            </Grid>
            <Grid item>
              <SelectFieldControl<UniqueOfdPosition> name="tax" label="Ставка НДС">
                {OFD_TAXES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </SelectFieldControl>
            </Grid>
            <Grid item>
              <SelectFieldControl<UniqueOfdPosition> name="paymentSubjectType" label="Предмет расчёта">
                {OFD_SUBJECT_TYPE.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </SelectFieldControl>
            </Grid>
            <Grid item>
              <SelectFieldControl<UniqueOfdPosition> name="paymentMethodType" label="Способ расчёта">
                {OFD_PAYMENT_METHODS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </SelectFieldControl>
            </Grid>
            <Grid item>
              <TextFieldControl name="nomenclatureCode" label="Код товарной номенклатуры" fullWidth />
            </Grid>
            <Grid item>
              <TextFieldControl name="unitOfMeasurement" label="Единица измерения предмета расчета" fullWidth />
            </Grid>
          </Grid>
        }
        actions={
          <>
            <Button fullWidth size="large" color="secondary" onClick={onClose}>
              Отмена
            </Button>
            <SubmitButton form="positionForm">{isEditingPosition ? 'Сохранить' : 'Добавить'}</SubmitButton>
          </>
        }
      />
    </Form>
  )
}
